import React from "react";
import LegalPage, { LegalContact } from "../components/LegalPage";
import { BUSINESS_NAME } from "../config";

// When the wording below changes, change this date too.
const UPDATED = "7 October 2026";

const SUMMARY = [
    "We keep records of the farmers who deliver milk to us so that we can record their milk and pay them correctly.",
    "Only our authorised staff can sign in and see those records.",
    "We do not sell information or use it for advertising.",
    "The system runs on servers outside Kenya, with providers that keep information safe and private.",
    "You can ask to see, correct or delete personal information about you.",
];

const SECTIONS = [
    {
        id: "who-we-are",
        title: "Who we are",
        body: (
            <>
                <p>
                    {BUSINESS_NAME} buys milk from dairy farmers and pays them for it. This website is the
                    system we use to record farmers, the milk they deliver and what they are paid. In this
                    policy "we" and "us" mean {BUSINESS_NAME}.
                </p>
                <p>
                    This policy covers this website and the Android app our field staff use to record milk.
                    It follows Kenya's Data Protection Act, 2019. We decide what is recorded and why, so we
                    are responsible for it.
                </p>
            </>
        ),
    },
    {
        id: "whose-information",
        title: "Whose information we hold",
        body: (
            <ul>
                <li><strong>Farmers</strong> who deliver milk to us.</li>
                <li><strong>Our staff</strong> who are given a sign-in to the system.</li>
                <li><strong>Visitors</strong> to this website.</li>
            </ul>
        ),
    },
    {
        id: "what-is-held",
        title: "What information is held",
        body: (
            <>
                <h3>Farmers</h3>
                <ul>
                    <li>Your names, phone number, national ID number and farmer number.</li>
                    <li>The M-Pesa number, or the bank or SACCO account, you are paid through.</li>
                    <li>Your collection route.</li>
                    <li>The milk you deliver: the date, the shift, the litres and who recorded them.</li>
                    <li>What you are paid, and any cash advance or goods given to you on credit and how they are repaid.</li>
                </ul>
                <p>Farmers do not sign in to this website. Our staff record this information.</p>

                <h3>Staff with a sign-in</h3>
                <ul>
                    <li>Name, email address, phone number, username and role.</li>
                    <li>A password. It is stored in a scrambled form that nobody can read.</li>
                    <li>Which records each person added or changed.</li>
                </ul>

                <h3>Visitors to the website</h3>
                <p>
                    Like most websites, our servers record technical details of each request, such as the
                    internet (IP) address and the time. We use this to keep the system secure and to find
                    faults. You do not have to give us any information to read the public pages.
                </p>
            </>
        ),
    },
    {
        id: "android-app",
        title: "The Android app",
        body: (
            <ul>
                <li>Our field staff record milk on the app. It keeps the farmers of the route being collected, and the milk recorded, on the phone until the collections are uploaded to the system.</li>
                <li>The app uses Bluetooth to talk to the weighing scale and the receipt printer. Android asks for the location permission before an app may search for Bluetooth devices. The app does not record or send where the phone is.</li>
                <li>The app does not read the phone's contacts, messages, photos or files.</li>
            </ul>
        ),
    },
    {
        id: "why",
        title: "Why we use it",
        body: (
            <>
                <ul>
                    <li><strong>To record your milk and pay you correctly.</strong> This is what our arrangement with each farmer is for.</li>
                    <li><strong>To keep our accounts,</strong> as tax and accounting law requires.</li>
                    <li><strong>To let our staff sign in,</strong> and to send them the emails that go with that, such as a password reset.</li>
                    <li><strong>To keep the system secure</strong> and look into misuse. We have a legitimate interest in doing so.</li>
                    <li><strong>To obey the law,</strong> for example a lawful request from an authority.</li>
                </ul>
                <p>We do not use anyone's details for advertising, and we do not sell them.</p>
            </>
        ),
    },
    {
        id: "who-sees-it",
        title: "Who can see it",
        body: (
            <ul>
                <li><strong>Our authorised staff.</strong> What each person can see and do depends on their role.</li>
                <li><strong>M-Pesa and banks,</strong> when we pay you: they receive the name, the number or account and the amount needed to make the payment.</li>
                <li><strong>Our software provider,</strong> the company that maintains the system for us, and <strong>the company that hosts our servers.</strong> They handle information only on our instructions and must keep it confidential.</li>
                <li><strong>Google,</strong> in two ways. The sign-in page can show Google's "I'm not a robot" check (reCAPTCHA), which sends Google your IP address and details of your browser. The website's lettering is loaded from Google Fonts, so your browser contacts Google.</li>
                <li><strong>Cloudflare,</strong> because the website's icons are loaded from its network, so your browser contacts it.</li>
                <li><strong>Authorities,</strong> when the law requires it.</li>
            </ul>
        ),
    },
    {
        id: "where",
        title: "Where it is kept",
        body: (
            <p>
                The system runs on servers outside Kenya that are provided by a hosting company. We use
                providers that protect information to the standard Kenyan law requires, and that are bound by
                contract to keep it confidential and secure.
            </p>
        ),
    },
    {
        id: "how-long",
        title: "How long it is kept",
        body: (
            <ul>
                <li><strong>Farmers:</strong> for as long as you deliver milk to us. After that we keep the record of your milk and payments for as long as tax and accounting law requires, and so that questions about past payments can be answered.</li>
                <li><strong>A farmer we remove from the list:</strong> no longer appears in the lists or on the phone, but the milk and payments already recorded stay, so that past records still add up.</li>
                <li><strong>Staff:</strong> for as long as the person works with us. The sign-in is removed when they leave; the record of what they entered stays.</li>
            </ul>
        ),
    },
    {
        id: "security",
        title: "How we protect it",
        body: (
            <>
                <ul>
                    <li>The website and the app talk to our servers over an encrypted connection.</li>
                    <li>Nobody sees the records without signing in, and roles limit what each person can do.</li>
                    <li>Passwords are stored in a scrambled form, and a sign-in ends by itself after a period.</li>
                    <li>The sign-in page can check that a person, not a program, is signing in.</li>
                </ul>
                <p>
                    No system can promise perfect security. If something goes wrong that puts your
                    information at risk, we tell the people affected and the Data Commissioner, as the law
                    requires.
                </p>
            </>
        ),
    },
    {
        id: "your-rights",
        title: "Your rights",
        body: (
            <>
                <p>Under the Data Protection Act you have the right to:</p>
                <ul>
                    <li>be told how your personal information is used;</li>
                    <li>see the information held about you and get a copy;</li>
                    <li>have information that is wrong or out of date corrected;</li>
                    <li>have information deleted when there is no longer a reason to keep it;</li>
                    <li>object to the way your information is used;</li>
                    <li>receive your information in a form you can take elsewhere;</li>
                    <li>not have decisions that affect you made by a computer alone. The system works out payments from the milk recorded and the price we set; our staff check them before we pay.</li>
                </ul>
                <p>
                    To use any of these rights, contact us using the details at the end of this page. We
                    answer within the time the law sets.
                </p>
                <p>
                    If you are not satisfied with our answer, you can complain to the Office of the Data
                    Protection Commissioner
                    (<a href="https://www.odpc.go.ke" target="_blank" rel="noopener noreferrer">www.odpc.go.ke</a>).
                </p>
            </>
        ),
    },
    {
        id: "browser-storage",
        title: "Cookies and what is kept in your browser",
        body: (
            <>
                <p>We do not use advertising cookies, and the website has no analytics that follow visitors.</p>
                <ul>
                    <li>When a member of staff signs in, the website keeps the sign-in, with their name and role, in their own browser until they log out or the sign-in ends.</li>
                    <li>Google's "I'm not a robot" check, when it is shown, may place its own cookies. Google's privacy policy applies to those.</li>
                </ul>
            </>
        ),
    },
    {
        id: "children",
        title: "Children",
        body: (
            <p>
                This system is for our staff and for the farmers who supply us, who are adults. We do not
                knowingly record information about anyone under 18.
            </p>
        ),
    },
    {
        id: "changes",
        title: "Changes to this policy",
        body: <p>When this policy changes we put the new version on this page with a new date.</p>,
    },
    {
        id: "contact",
        title: "Contact us",
        body: (
            <>
                <p>For any question about this policy or about your information:</p>
                <LegalContact />
            </>
        ),
    },
];

function PrivacyPolicy() {
    return (
        <LegalPage
            title="Privacy Policy"
            intro="What information we hold, why, who can see it, and the choices you have."
            updated={UPDATED}
            summary={SUMMARY}
            sections={SECTIONS}
            other={{ to: "/terms-and-conditions", label: "Terms and Conditions" }}
        />
    );
}

export default PrivacyPolicy;
